import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import './SparkPage.css';

const CATEGORIES = [
  {
    label: 'Category A', age: '8 – 10 Years', icon: '🌱',
    color: '#f59e0b', light: '#fffbeb', border: '#fde68a',
    theme: 'Basic Electronics & Simple Robotics',
    desc: 'Young innovators build simple circuits and basic robotic models using beginner-friendly kits and components.',
    skills: ['Basic Circuits', 'Simple Robotics', 'Creative Thinking', 'Team Work'],
  },
  {
    label: 'Category B', age: '11 – 13 Years', icon: '⚡',
    color: '#2563eb', light: '#eff6ff', border: '#bfdbfe',
    theme: 'Sensors & Microcontroller Projects',
    desc: 'Intermediate builders work with sensors, Arduino and automation to create smart, functional projects.',
    skills: ['Arduino', 'Sensors', 'Automation', 'Problem Solving'],
    featured: true,
  },
  {
    label: 'Category C', age: '14+ Years', icon: '🚀',
    color: '#7c3aed', light: '#f5f3ff', border: '#ddd6fe',
    theme: 'IoT, AI & Advanced Hardware',
    desc: 'Advanced innovators tackle real-world problems using IoT, AI hardware, and complex system builds.',
    skills: ['IoT', 'AI Hardware', 'System Design', 'Innovation'],
  },
];

const STEPS = [
  { n: '01', title: 'Register Online', desc: 'Fill the registration form with your name, school and age category details.' },
  { n: '02', title: 'Receive Problem Statement', desc: 'Get your category-specific problem statement and hardware guidelines from iBots.' },
  { n: '03', title: 'Build Your Project', desc: 'Design and build your hardware innovation using the provided components and kits.' },
  { n: '04', title: 'Submit Video Demo', desc: 'Record a short working demo of your project and submit it before the deadline.' },
  { n: '05', title: 'Present to Judges', desc: 'Present your project live on hackathon day to a panel of expert judges.' },
  { n: '06', title: 'Win & Get Recognised', desc: 'Top teams win prizes, trophies and national-level iBots certificates.' },
];

const CRITERIA = [
  { icon: '💡', title: 'Innovation', desc: 'Unique hardware idea that creatively addresses a real-world problem.' },
  { icon: '⚙️', title: 'Functionality', desc: 'The project works correctly and demonstrates a complete solution.' },
  { icon: '🌍', title: 'Problem Solving', desc: 'Addresses a real challenge with measurable impact.' },
  { icon: '🎤', title: 'Presentation', desc: 'Clear explanation of the idea, build process and final outcome.' },
];

const FAQS = [
  { q: 'What is ibots Spark 1.0?', a: 'ibots Spark 1.0 is the National School Hardware Hackathon Series — India\'s premier platform for school students to build real hardware projects and win national recognition.' },
  { q: 'Who can participate?', a: 'School students across India in age groups 8–10, 11–13 and 14+ years can participate individually or in teams.' },
  { q: 'What kind of projects can students build?', a: 'Students can build hardware projects using circuits, sensors, microcontrollers, Arduino, IoT modules and robotics kits based on their category.' },
  { q: 'Can students participate as a team?', a: 'Yes, students can participate individually or in teams. Team size details are shared upon registration.' },
  { q: 'How are projects judged?', a: 'Projects are evaluated on innovation, functionality, problem-solving and quality of presentation by expert iBots judges.' },
  { q: 'What do winners receive?', a: 'Winners receive trophies, medals, cash prizes, iBots kits and national-level certificates. All participants receive participation certificates.' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="sp-faq-item" onClick={() => setOpen(!open)}>
      <div className="sp-faq-q">
        <span>{q}</span>
        <span className="sp-faq-icon">{open ? '−' : '+'}</span>
      </div>
      {open && <div className="sp-faq-a">{a}</div>}
    </div>
  );
}

export default function SparkPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name:'', phone:'', email:'', school:'', city:'', category:'', message:'' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  function validate() {
    const e = {};
    if (!form.name.trim())   e.name     = 'Name is required';
    if (!form.phone.trim())  e.phone    = 'Phone is required';
    if (!form.email.trim())  e.email    = 'Email is required';
    if (!form.school.trim()) e.school   = 'School name is required';
    if (!form.category)      e.category = 'Please select your age category';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      await fetch('http://localhost:5000/api/hackathon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, formType: 'ibots-spark-1.0', createdAt: new Date() }),
      });
    } catch (err) {}
    setSubmitted(true);
  }

  return (
    <div className="sp-page">
      <Header />
      <Navbar />

      {/* ── HERO ── */}
      <section className="sp-hero">
        <div className="sp-hero__bg">
          <img src="https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=1600&q=80" alt="ibots Spark 1.0" className="sp-hero__img" />
          <div className="sp-hero__overlay" />
        </div>
        <div className="sp-hero__content">
          <div className="sp-hero__eyebrow">
            <span className="sp-hero__dot" />
            National School Hardware Hackathon Series
          </div>
          <h1 className="sp-hero__title">ibots <span className="sp-hero__yellow">Spark 1.0</span></h1>
          <p className="sp-hero__sub">Where young innovators build the future with hardware</p>
          <div className="sp-hero__cats">
            {[{ age:'8–10', label:'Cat A', icon:'🌱' }, { age:'11–13', label:'Cat B', icon:'⚡' }, { age:'14+', label:'Cat C', icon:'🚀' }].map((c,i) => (
              <div key={i} className="sp-hero__cat">
                <span>{c.icon}</span>
                <span className="sp-hero__cat-age">{c.age}</span>
                <span className="sp-hero__cat-lbl">{c.label}</span>
              </div>
            ))}
          </div>
          <div className="sp-hero__btns">
            <a href="#sp-register" className="sp-btn-primary">Register Now →</a>
            <button className="sp-btn-secondary" onClick={() => navigate('/hackathon-support')}>← Back to Hackathon Support</button>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section className="sp-section sp-about">
        <div className="sp-container">
          <div className="sp-about__grid">
            <div className="sp-about__text">
              <div className="sp-label">ABOUT THE EVENT</div>
              <h2 className="sp-title">What is ibots Spark 1.0?</h2>
              <p className="sp-para">ibots Spark 1.0 is India's National School Hardware Hackathon Series — a premier platform designed to inspire school students to move beyond textbooks and build real-world hardware innovations.</p>
              <p className="sp-para">With three age-appropriate categories, every student gets a fair chance to showcase their creativity, technical thinking and problem-solving ability in front of expert judges.</p>
              <div className="sp-about__pills">
                {['Open Pan-India', 'School Students Only', '3 Age Categories', 'Hardware Focused', 'Expert Judges', 'National Recognition'].map(p => (
                  <span key={p} className="sp-pill">{p}</span>
                ))}
              </div>
            </div>
            <div className="sp-about__img-wrap">
              <img src="https://images.unsplash.com/photo-1559028012-481c04fa702d?w=800&q=80" alt="ibots Spark" className="sp-about__img" />
              <div className="sp-about__img-badge">
                <span className="sp-about__img-badge-num">1.0</span>
                <span className="sp-about__img-badge-text">Edition</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AGE CATEGORIES ── */}
      <section className="sp-section sp-cats-section">
        <div className="sp-container">
          <div className="sp-center-header">
            <div className="sp-label">AGE CATEGORIES</div>
            <h2 className="sp-title">Tailored for Every Age Group</h2>
            <p className="sp-sub">Open for school students across India with age-appropriate challenges and problem statements</p>
          </div>
          <div className="sp-cats-grid">
            {CATEGORIES.map((cat, i) => (
              <div key={i} className={`sp-cat-card ${cat.featured ? 'sp-cat-card--featured' : ''}`}
                style={{ '--cc': cat.color, '--cl': cat.light, '--cb': cat.border }}>
                {cat.featured && <div className="sp-cat-popular">Most Popular</div>}
                <div className="sp-cat-top" style={{ borderBottom:`3px solid ${cat.color}`, paddingBottom:20, marginBottom:20 }}>
                  <div className="sp-cat-icon-wrap" style={{ background:cat.light, border:`2px solid ${cat.border}` }}>
                    <span className="sp-cat-icon">{cat.icon}</span>
                  </div>
                  <div className="sp-cat-tag" style={{ background:cat.color }}>{cat.label}</div>
                  <div className="sp-cat-age" style={{ color:cat.color }}>{cat.age}</div>
                  <div className="sp-cat-theme">{cat.theme}</div>
                </div>
                <p className="sp-cat-desc">{cat.desc}</p>
                <div className="sp-cat-skills">
                  {cat.skills.map(s => <span key={s} className="sp-cat-skill" style={{ background:cat.light, color:cat.color, border:`1px solid ${cat.border}` }}>{s}</span>)}
                </div>
                <a href="#sp-register" className="sp-cat-btn" style={{ background:cat.color }}>Register →</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW TO PARTICIPATE ── */}
      <section className="sp-section sp-steps-section">
        <div className="sp-container">
          <div className="sp-center-header">
            <div className="sp-label">PARTICIPATION</div>
            <h2 className="sp-title">How to Participate</h2>
            <p className="sp-sub">Simple steps to join ibots Spark 1.0 and compete at the national level</p>
          </div>
          <div className="sp-steps-grid">
            {STEPS.map((s, i) => (
              <div key={i} className="sp-step-card">
                <div className="sp-step-num">{s.n}</div>
                <h3 className="sp-step-title">{s.title}</h3>
                <p className="sp-step-desc">{s.desc}</p>
                {i < STEPS.length - 1 && <div className="sp-step-arrow">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── JUDGING CRITERIA ── */}
      <section className="sp-section sp-criteria-section">
        <div className="sp-container">
          <div className="sp-center-header">
            <div className="sp-label">EVALUATION</div>
            <h2 className="sp-title">Judging Criteria</h2>
            <p className="sp-sub">Every project is evaluated across four key dimensions by our expert panel</p>
          </div>
          <div className="sp-criteria-grid">
            {CRITERIA.map((c, i) => (
              <div key={i} className="sp-criteria-card">
                <div className="sp-criteria-icon">{c.icon}</div>
                <h3 className="sp-criteria-title">{c.title}</h3>
                <p className="sp-criteria-desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REGISTRATION FORM ── */}
      <section className="sp-section sp-form-section" id="sp-register">
        <div className="sp-container">
          <div className="sp-center-header">
            <div className="sp-label">REGISTER</div>
            <h2 className="sp-title">Register for ibots Spark 1.0</h2>
            <p className="sp-sub">Fill in the details below — our team will get back to you within 24 hours</p>
          </div>
          {submitted ? (
            <div className="sp-success">
              <div className="sp-success-icon">✅</div>
              <h3>Registration Submitted!</h3>
              <p>Thank you for registering for ibots Spark 1.0. Our team will contact you shortly with all the details.</p>
            </div>
          ) : (
            <form className="sp-form" onSubmit={handleSubmit}>
              <div className="sp-form-grid">
                <div className="sp-field">
                  <label>Full Name *</label>
                  <input value={form.name} onChange={e => { setForm({...form, name:e.target.value}); setErrors({...errors, name:''}); }} placeholder="Student / Parent name" />
                  {errors.name && <span className="sp-err">{errors.name}</span>}
                </div>
                <div className="sp-field">
                  <label>Phone Number *</label>
                  <input value={form.phone} onChange={e => { setForm({...form, phone:e.target.value}); setErrors({...errors, phone:''}); }} placeholder="+91 XXXXX XXXXX" />
                  {errors.phone && <span className="sp-err">{errors.phone}</span>}
                </div>
                <div className="sp-field">
                  <label>Email Address *</label>
                  <input type="email" value={form.email} onChange={e => { setForm({...form, email:e.target.value}); setErrors({...errors, email:''}); }} placeholder="your@email.com" />
                  {errors.email && <span className="sp-err">{errors.email}</span>}
                </div>
                <div className="sp-field">
                  <label>School Name *</label>
                  <input value={form.school} onChange={e => { setForm({...form, school:e.target.value}); setErrors({...errors, school:''}); }} placeholder="Your school name" />
                  {errors.school && <span className="sp-err">{errors.school}</span>}
                </div>
                <div className="sp-field">
                  <label>City</label>
                  <input value={form.city} onChange={e => setForm({...form, city:e.target.value})} placeholder="City" />
                </div>
                <div className="sp-field">
                  <label>Age Category *</label>
                  <select value={form.category} onChange={e => { setForm({...form, category:e.target.value}); setErrors({...errors, category:''}); }}>
                    <option value="">Select your category</option>
                    <option value="8-10">Category A — 8 to 10 Years</option>
                    <option value="11-13">Category B — 11 to 13 Years</option>
                    <option value="14+">Category C — 14+ Years</option>
                  </select>
                  {errors.category && <span className="sp-err">{errors.category}</span>}
                </div>
                <div className="sp-field sp-field--full">
                  <label>Message / Questions</label>
                  <textarea value={form.message} onChange={e => setForm({...form, message:e.target.value})} rows={3} placeholder="Any questions or additional info..." />
                </div>
              </div>
              <button type="submit" className="sp-submit">Register for ibots Spark 1.0 →</button>
            </form>
          )}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="sp-section sp-faq-section">
        <div className="sp-container sp-faq-wrap">
          <div className="sp-center-header">
            <div className="sp-label">FAQ</div>
            <h2 className="sp-title">Frequently Asked Questions</h2>
          </div>
          <div className="sp-faq-list">
            {FAQS.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      <Footer ctaHeading={"Ready to Spark Innovation?"} ctaSub={"Join ibots Spark 1.0 — India's National School Hardware Hackathon Series."} />
    </div>
  );
}
